<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Task"
      text="Edit Task"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row
          v-if="skeleton"
          no-gutters
        >
          <v-col cols="12">
            <v-skeleton-loader
              type="paragraph,list-item, list-item,list-item,list-item,list-item"
            />
          </v-col>
        </v-row>
        <v-row
          v-else
          no-gutters
        >
          <v-col cols="12">
            <v-textarea
              v-model="content"
              v-validate="'required'"
              outlined
              label="Task Content"
              :rows="4"
              auto-grow
              class="mt-4"
              data-vv-name="task content"
              :error-messages="errors.collect('task content')"
            />
          </v-col> 
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="priority"
              v-validate="'required'"
              :error-messages="errors.collect('priority')"
              data-vv-name="priority"
              label="Priority"
              item-value="id"
              item-text="name"
              :items="priorities"
              hide-no-data
              hide-selected
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="category"
              v-validate="'required'"
              :error-messages="errors.collect('category')"
              data-vv-name="category"
              label="Category"
              item-value="id"
              item-text="name"
              :items="taskCategories"
              hide-no-data
              hide-selected
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedAssignee"
              v-validate="'required'"
              :error-messages="errors.collect('assignee')"
              data-vv-name="assignee"
              label="Assignees"
              item-value="employee_uid"
              item-text="user.name"
              :items="subordinates"
              :loading="assigneeLoading"
              :search-input.sync="searchAssignee"
              multiple
              chips
              clearable
              hide-no-data
              hide-selected
              deletable-chips
              @change="clearViewerIds"
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedViewer"
              :error-messages="errors.collect('viewer')"
              data-vv-name="viewer"
              label="Viewers"
              item-value="employee_uid"
              item-text="user.name"
              :items="filteredObservers"
              :loading="viewerLoading"
              :search-input.sync="searchViewer"
              multiple
              chips
              clearable
              hide-no-data
              hide-selected
              deletable-chips
              @change="clearAssigneeIds"
            />
          </v-col> 

          <v-col cols="12">
            <v-menu
              v-model="date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="due_date"
                  label="Due date"
                  prepend-icon="event"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="due_date"
                no-title
                @input="date_menu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    props: {
      task: {
        type: Object,
        required: true,
      },
      employeeUid: {
        type: String,
        default: null,
      },
    },

    data () {
      return {
        dialog: null,
        localTask: null,
        content: null,
        priority: null,
        category: null,
        due_date: null,
        searchAssignee: null,
        searchViewer: null,
        date_menu: false,
        assigneeLoading: false, 
        viewerLoading: false, 
        progressing: false,
        skeleton: false,
        priorities: [],
        taskCategories: [],
        subordinates: [],
        selectedAssignee: [],
        selectedViewer: [],
      }
    },

    computed: {
      filteredObservers () {
        if (!this.selectedAssignee) return this.subordinates
     
        return this.subordinates.filter(subordinate => {
          return !this.selectedAssignee.includes(subordinate.employee_uid)
        })
      },
    },

    watch: {
      dialog: function (value) {
        if (value) {
          this.skeleton = true
          Promise.all([
            this.$api.subordinates().recursiveList(this.employeeUid),
            this.$api.taskPriorities().list(),
            this.$api.taskCategories().list(),
          ])
            .then(([employee, priority, taskCategory]) => {
              let subordinatesUids = _.map(employee.data, 'employee_uid')

              this.subordinates = _.orderBy(employee.data, ['user.name'], ['asc'])
              this.viewers = this.subordinates 
              this.priorities = priority.data
              this.taskCategories = taskCategory.data

              this.localTask = { ...this.task }
              this.content = this.localTask.content
              this.priority = this.localTask.priority.id
              this.category = this.localTask.task_category.id
              this.due_date = this.localTask.due_date

              this.localTask.assignees.map(assignee => {
                if (!subordinatesUids.includes(assignee.assignee_uid)) {
                  // call function to search and add into employee.data
                  this.$api.employees().searchName(assignee.assignee_uid)
                    .then(res => {
                      res.data.map(data => {
                        this.subordinates.push({ employee_uid: data.employee_uid, user: { name: data.user.name } })
                      })
                    })
                }
                this.selectedAssignee.push(assignee.assignee_uid)
              })

              this.localTask.viewers.map(viewer => {
                if (!subordinatesUids.includes(viewer.viewer_uid)) {
                  // call function to search and add into employee.data
                  this.$api.employees().searchName(viewer.viewer_uid)
                    .then(res => {
                      res.data.map(data => {
                        this.subordinates.push({ employee_uid: data.employee_uid, user: { name: data.user.name } })
                      })
                    })
                }
                this.selectedViewer.push(viewer.viewer_uid)
              })
              this.skeleton = false
            })
        } else {
          this.selectedAssignee = []
          this.selectedViewer = []
        }
      },

      searchAssignee (val) {
        this.subordinates.map(subordinate => {
          if (subordinate.user.name === val) return true
        })
    
        this.assigneeLoading = true
        this.fetchAssigneeEntriesDebounced(val)
      },

      searchViewer (val) {
        this.subordinates.map(subordinate => {
          if (subordinate.user.name === val) return true
        })
    
        this.viewerLoading = true
        this.fetchViewerEntriesDebounced(val)
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.progressing = true
            this.$api.ownerTasks().update(this.task.id, this.prepareFormData())
              .then(res => {
                this.$emit('updated')
                this.$snack({ message: 'Task updated succesfully' })
                this.dialog = false
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },

      fetchViewerEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.searchEmployee(val)
        }, 500) /* 500ms throttle */
      },

      fetchAssigneeEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.searchEmployee(val)
        }, 500) /* 500ms throttle */
      },

      searchEmployee (val) {
        this.$api.employees().search({ name: val })
          .then(response => {
            this.assigneeLoading = false
            this.viewerLoading = false
            response.data.map(data => {
              this.subordinates.push({ employee_uid: data.employee.employee_uid, user: { name: data.name } })
            })
          })
      },
      prepareFormData () {
        return {
          'assignee_uids': JSON.stringify(this.selectedAssignee),
          'viewer_uids': JSON.stringify(this.selectedViewer),
          'content': this.content,
          'due_date': this.due_date,
          'priority_id': this.priority,
          'task_category_id': this.category,
        }
      },

      clearViewerIds () {
        this.selectedViewer = this.selectedViewer.filter(x => !this.selectedAssignee.includes(x))
      },
      clearAssigneeIds () {
        this.selectedAssignee = this.selectedAssignee.filter(x => !this.selectedViewer.includes(x))
      },
    },
  }
</script>
